import * as THREE from 'three';
import { SectionConfig, SectionRect, SectionInterface, SectionInsets } from '../types';
import { createSash } from '../geometry/SashGeometry';
import { createGlassPane } from '../geometry/GlassGeometry';
import { createHandle } from '../geometry/HandleGeometry';
import { createRoedenPattern } from '../geometry/RoedenGeometry';
import { MaterialLibrary } from '../materials/MaterialLibrary';
import { PROFILE } from '../utils/profileConstants';

/**
 * SLIDE-V: Vertical sliding sash (schuifraam verticaal / heritage sash).
 * Bottom sash slides up on a track.
 */
export class SlideVSection implements SectionInterface {
  readonly group: THREE.Group;
  private slideGroup: THREE.Group;
  private maxSlide: number;
  private slideBaseY: number;

  constructor(config: SectionConfig, rect: SectionRect, materialLib: MaterialLibrary, frameMaterial: THREE.Material, insets: SectionInsets) {
    this.group = new THREE.Group();
    this.group.name = `section-slidev-${rect.index}`;
    this.group.userData = { sectionIndex: rect.index, sectionType: 'SLIDE-V' };

    // Effective dimensions after insetting from adjacent mullions
    const effW = rect.width - insets.left - insets.right;
    const effH = rect.height - insets.top - insets.bottom;
    const panelH = effH / 2;
    const clearW = effW - 2 * PROFILE.sashWidth;
    const clearH = panelH - 2 * PROFILE.sashWidth;
    this.maxSlide = panelH;

    const glassMat = materialLib.glass(config.infill.tint, config.infill.glazingTier);

    // Each panel gets half the sash depth so together they span the same
    // total depth as a single sash on any other section type.
    // Fixed panel sits on the back track, sliding panel on the front track.
    const halfDepth = PROFILE.sashDepth / 2;

    // Fixed panel (top half) — back track
    const fixedSash = createSash(effW, panelH, PROFILE.sashWidth, halfDepth, frameMaterial);
    fixedSash.position.set(insets.left, insets.bottom + panelH, -halfDepth / 2);
    this.group.add(fixedSash);

    const fixedGlass = createGlassPane(clearW, clearH, PROFILE.glassThickness, glassMat);
    fixedGlass.position.set(insets.left + effW / 2, insets.bottom + effH - panelH / 2, -halfDepth / 2);
    this.group.add(fixedGlass);

    // Sliding panel (bottom half) — front track, moves up
    this.slideGroup = new THREE.Group();
    this.slideGroup.name = 'slide-panel';

    const slideSash = createSash(effW, panelH, PROFILE.sashWidth, halfDepth, frameMaterial);
    this.slideGroup.add(slideSash);

    const slideGlass = createGlassPane(clearW, clearH, PROFILE.glassThickness, glassMat);
    slideGlass.position.set(effW / 2, panelH / 2, 0);
    this.slideGroup.add(slideGlass);

    if (config.roeden.pattern !== 'none' && config.roeden.mode !== 'none') {
      const roeden = createRoedenPattern(
        clearW, clearH, config.roeden,
        materialLib.roedenMaterial(),
      );
      roeden.position.set(effW / 2, panelH / 2, 0);
      this.slideGroup.add(roeden);
    }

    const handle = createHandle(materialLib.handleMaterial(config.hardware.handleFinish));
    handle.position.set(effW / 2, panelH - PROFILE.sashWidth / 2, halfDepth / 2);
    handle.rotation.z = Math.PI;
    this.slideGroup.add(handle);

    this.slideBaseY = insets.bottom;
    this.slideGroup.position.set(insets.left, this.slideBaseY, halfDepth / 2);
    this.group.add(this.slideGroup);

    if (config.openAmount > 0) {
      this.setTranslation(config.openAmount * this.maxSlide);
    }

    this.group.position.set(rect.x, rect.y, 0);
  }

  setTranslation(t: number): void {
    this.slideGroup.position.y = this.slideBaseY + t;
  }

  setOpenAngle(rad: number): void {
    this.setTranslation(rad / (Math.PI / 2) * this.maxSlide);
  }
}
